// The hours report, turned on its side.
//
// The feed is one row per person per day with five measures on it, which
// reads as a wall of repeated names. What anyone actually wants to see is a
// person's week across the page: the dates as columns, a line per measure,
// and a total at the end.
//
// A measure with nothing in it for that person over the whole range is not
// shown at all — most staff do not teach, and a column of dashes says
// nothing.

import { formatHours } from "./hours";

export const MEASURES = [
  { key: "scheduledMinutes", label: "scheduled" },
  { key: "teachingMinutes", label: "teaching" },
  { key: "otherMinutes", label: "other" },
  { key: "approvedOvertimeMinutes", label: "overtime approved" },
  { key: "unapprovedOvertimeMinutes", label: "overtime pending" },
];

const iso = (d) => String(d || "").slice(0, 10);

/** Every date in [from, to], so a day nobody worked is still a column. */
export function datesBetween(from, to) {
  const start = iso(from);
  const end = iso(to);
  if (!start || !end || start > end) return [];

  const out = [];
  const cursor = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  // Formatted from the local parts, never through toISOString: local midnight
  // in British Summer Time is the previous day in UTC, which would shift
  // every column back by one for half the year.
  const stamp = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  // A range is a window someone chose; a runaway one is a mistake, not a report.
  for (let guard = 0; cursor <= last && guard < 400; guard++) {
    out.push(stamp(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/**
 * One row per person per measure, with a cell per date and a total.
 *
 * People are ordered by name; their measures keep the order above, so the
 * eye can run down the page and find the same line in the same place.
 */
export function pivotHours(rows = [], dates = []) {
  const byStaff = new Map();

  rows.forEach((r) => {
    const key = r?.staffId ?? r?.name;
    if (key == null) return;
    if (!byStaff.has(key)) {
      byStaff.set(key, { staffId: r.staffId, name: r.name || "—", byDate: new Map() });
    }
    byStaff.get(key).byDate.set(iso(r.date), r);
  });

  const people = [...byStaff.values()].sort((a, b) =>
    String(a.name).localeCompare(String(b.name))
  );

  const out = [];
  people.forEach((person) => {
    MEASURES.forEach((measure) => {
      const values = dates.map((date) => {
        const row = person.byDate.get(date);
        return Number(row?.[measure.key]) || 0;
      });
      const total = values.reduce((sum, v) => sum + v, 0);
      // Nothing to show all range: the line would be dashes and a zero.
      if (total === 0) return;
      out.push({
        staffId: person.staffId,
        name: person.name,
        measure: measure.label,
        label: `${person.name} — ${measure.label}`,
        values,
        total,
      });
    });
  });

  return out;
}

/** Column totals across everyone, and the total of those. */
export function columnTotals(pivoted = [], dates = []) {
  const perDate = dates.map((_, i) =>
    pivoted.reduce((sum, row) => sum + (row.values[i] || 0), 0)
  );
  return { perDate, total: perDate.reduce((sum, v) => sum + v, 0) };
}

/**
 * The same table as a CSV. Minutes, not "8h 30m": a spreadsheet should get
 * numbers it can add up, and the heading says which unit they are in.
 */
export function hoursCsv({ pivoted = [], dates = [], campus = "", from = "", to = "" }) {
  const header = ["Staff", "Measure", ...dates, "Total"];
  const body = pivoted.map((row) => [row.name, row.measure, ...row.values, row.total]);
  const totals = columnTotals(pivoted, dates);
  const footer = ["All staff", "total", ...totals.perDate, totals.total];

  const title = [`Hours (minutes) — ${campus || "all campuses"}, ${from} to ${to}`];
  return [title, header, ...body, footer]
    .map((line) => line.map((cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`).join(","))
    .join("\n");
}

export function hoursCsvFilename({ campus, from, to }) {
  const slug = String(campus || "all-campuses").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return `hours_${slug}_${from}_to_${to}.csv`;
}

/** For the screen: a cell reads as hours, and an empty day as a dash. */
export const cell = (minutes) => formatHours(minutes);

/**
 * The campuses to offer: one entry per name, whether or not a season is
 * running there now. The feed carries a row per campus per season, so the
 * same name appears many times; hours are asked for by name.
 *
 * Those running now come first, since that is usually what someone wants,
 * and the rest follow in alphabetical order rather than being hidden.
 */
export function campusChoices(rows = []) {
  const byName = new Map();
  rows.forEach((c) => {
    const name = String(c?.name || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    const seen = byName.get(key);
    byName.set(key, { name: seen?.name || name, active: Boolean(seen?.active || c.active) });
  });

  return [...byName.values()].sort(
    (a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name)
  );
}
